import { useEffect, useState, useCallback } from "react";
import pullAt from "lodash/pullAt";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDispatch } from "react-redux";
import { hideModal } from "actions/modal";
import { ADD_COLLECTION_ROUTINE } from "constants/ActionTypes";

interface Collection {
  _id?: string;
  name: string;
  extAuth: boolean;
  ext_auth?: string[];
}

interface User {
  description: string;
  read: boolean;
  update: boolean;
  create: boolean;
}

interface NewCollectionModalProps {
  collection?: Collection;
  users?: User[];
  loading?: boolean;
}

const initialCollection: Collection = {
  name: "",
  extAuth: true,
};

const initialUser: User = {
  description: "",
  read: false,
  update: false,
  create: false,
};

const NewCollectionModal = ({
  collection,
  users,
  loading = false,
}: NewCollectionModalProps) => {
  const dispatch = useDispatch();

  const [collectionCopy, setCollectionCopy] = useState<Collection>({
    ...initialCollection,
  });
  const [usersCopy, setUsersCopy] = useState<User[]>([{ ...initialUser }]);

  useEffect(() => {
    if (collection) {
      setCollectionCopy({
        ...collection,
        extAuth: Boolean(collection.ext_auth && collection.ext_auth.length > 0),
      });
    }

    if (users) {
      setUsersCopy(users);
    }
  }, [collection, users]);

  const handleClose = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  const submitForm = useCallback(() => {
    dispatch({
      type: ADD_COLLECTION_ROUTINE.TRIGGER,
      payload: {
        collection: collectionCopy,
        users: usersCopy,
      },
    });
  }, [dispatch, collectionCopy, usersCopy]);

  const removeUser = (index: number) => {
    const users = [...usersCopy];
    pullAt(users, [index]);
    setUsersCopy(users);
  };

  const updateUser = (
    index: number,
    field: keyof User,
    value: string | boolean
  ) => {
    const users = [...usersCopy];
    (users[index] as Record<keyof User, string | boolean>)[field] = value;
    setUsersCopy(users);
  };

  const addUser = () => {
    setUsersCopy([...usersCopy, { ...initialUser }]);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[600px] p-0" showCloseButton={false}>
        <div className="flex flex-col">
          {/* Header */}
          <DialogHeader className="px-6 py-3">
            <DialogTitle className="font-gotham text-lg font-semibold text-neutral-900">
              New Collection
            </DialogTitle>
            <DialogDescription className="font-inter text-sm text-neutral-600 mt-2">
              Create a new collection to organize your media assets.
            </DialogDescription>
          </DialogHeader>

          {/* Content */}
          <DialogBody className="px-6 pb-6 space-y-4 max-h-[60vh]">
            {/* Collection Name */}
            <div className="space-y-1.5">
              <label className="font-gotham text-sm font-semibold text-neutral-700">
                Collection Name
              </label>
              <Input
                type="text"
                name="collection"
                placeholder="Enter collection name (optional)"
                value={collectionCopy.name}
                onChange={(e) =>
                  setCollectionCopy({
                    ...collectionCopy,
                    name: e.target.value,
                  })
                }
                className="border-neutral-200"
              />
            </div>

            {/* External Access Checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="ext-auth"
                checked={collectionCopy.extAuth}
                onCheckedChange={(checked) =>
                  setCollectionCopy({
                    ...collectionCopy,
                    extAuth: checked === true,
                  })
                }
              />
              <label
                htmlFor="ext-auth"
                className="font-inter text-sm text-neutral-700 cursor-pointer"
              >
                Allow External User Access
              </label>
            </div>

            {/* External Users Section */}
            {collectionCopy.extAuth && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <label className="font-gotham text-sm font-semibold text-neutral-700">
                    Collection External Users
                  </label>
                  <Button variant="blue-secondary" size="sm" onClick={addUser}>
                    <i className="fa-regular fa-plus h-4 w-4 mr-1" />
                    Add User
                  </Button>
                </div>

                {/* Users Table */}
                <div className="border border-neutral-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-neutral-50 border-b border-neutral-200">
                        <th className="font-gotham text-xs font-medium text-neutral-600 text-left px-3 py-2">
                          Name
                        </th>
                        <th className="font-gotham text-xs font-medium text-neutral-600 text-center px-3 py-2 w-16">
                          Read
                        </th>
                        <th className="font-gotham text-xs font-medium text-neutral-600 text-center px-3 py-2 w-16">
                          Create
                        </th>
                        <th className="font-gotham text-xs font-medium text-neutral-600 text-center px-3 py-2 w-16">
                          Update
                        </th>
                        <th className="w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersCopy.map((user, index) => (
                        <tr
                          key={index}
                          className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50"
                        >
                          <td className="px-3 py-2">
                            <Input
                              placeholder="Enter name"
                              name={`collection-user-${index}`}
                              value={user.description}
                              onChange={(e) =>
                                updateUser(index, "description", e.target.value)
                              }
                              className="border-neutral-200 h-8 text-sm"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Checkbox
                              checked={user.read}
                              onCheckedChange={(checked) =>
                                updateUser(index, "read", checked === true)
                              }
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Checkbox
                              checked={user.create}
                              onCheckedChange={(checked) =>
                                updateUser(index, "create", checked === true)
                              }
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Checkbox
                              checked={user.update}
                              onCheckedChange={(checked) =>
                                updateUser(index, "update", checked === true)
                              }
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeUser(index)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                            >
                              <i className="fa-regular fa-trash-can h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </DialogBody>

          {/* Footer */}
          <DialogFooter className="flex flex-row items-center justify-between sm:justify-between border-t border-neutral-200 px-6 py-3">
            <Button
              variant="ghost"
              disabled={loading}
              onClick={handleClose}
              className="text-neutral-600 hover:text-neutral-700 hover:bg-neutral-100"
            >
              Cancel
            </Button>
            <Button
              variant="blue-primary"
              disabled={loading}
              onClick={submitForm}
            >
              {loading ? (
                <>
                  <Spinner size="sm" variant="inverted" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewCollectionModal;
